/**
 * The queue, running.
 *
 * One item at a time, on purpose. A workshop connection that can carry one
 * 26 KB photograph often cannot carry eight at once, and parallel uploads on a
 * bad link fail together and then all back off together. Serial is slower on a
 * good connection and far more likely to finish on a bad one, which is the
 * connection this product is designed around.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  markDone, markFailed, markUploading, nextToUpload, summarise,
  type QueueItem, type QueueStore, type QueueSummary,
} from './queue.js';
import { uploadPhoto, type UploadDeps } from './upload.js';
import { createQueueStore } from './queueStore.js';

export interface UseUploadQueue {
  items: QueueItem[];
  summary: QueueSummary;
  online: boolean;
  enqueue(item: Omit<QueueItem, 'state' | 'attempts' | 'lastError' | 'nextAttemptAt'>): Promise<void>;
  /** Clear a failed item so the estimator can retake the shot. */
  discard(id: string): Promise<void>;
}

const TICK_MS = 2_000;

export function useUploadQueue(deps: UploadDeps, store?: QueueStore): UseUploadQueue {
  const storeRef = useRef<QueueStore>(store ?? createQueueStore());
  const [items, setItems] = useState<QueueItem[]>([]);
  const [online, setOnline] = useState(
    () => (typeof navigator === 'undefined' ? true : navigator.onLine));
  const busy = useRef(false);

  // Load whatever survived the last session before anything else happens.
  useEffect(() => {
    void storeRef.current.all().then(setItems);
  }, []);

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);

  const save = useCallback(async (item: QueueItem) => {
    await storeRef.current.put(item);
    setItems((prev) => {
      const i = prev.findIndex((x) => x.id === item.id);
      if (i === -1) return [...prev, item];
      const next = [...prev];
      next[i] = item;
      return next;
    });
  }, []);

  const enqueue = useCallback<UseUploadQueue['enqueue']>(async (partial) => {
    await save({
      ...partial, state: 'pending', attempts: 0, lastError: null, nextAttemptAt: 0,
    });
  }, [save]);

  const discard = useCallback(async (id: string) => {
    await storeRef.current.remove(id);
    setItems((prev) => prev.filter((x) => x.id !== id));
  }, []);

  // The pump. `busy` is a ref rather than state because it must be read and set
  // within one tick — a state update would not have landed before the next
  // interval fires, and the same photograph would upload twice.
  useEffect(() => {
    const tick = async () => {
      if (busy.current) return;
      const item = nextToUpload(items, Date.now(), online);
      if (!item) return;

      busy.current = true;
      try {
        await save(markUploading(item));
        const out = await uploadPhoto(
          { url: '', width: item.width, height: item.height, bytes: item.blob.size,
            tokens: 0, takenAt: item.takenAt },
          item.blob,
          { shopId: item.shopId, jobId: item.jobId, slot: item.slot },
          deps,
        );

        if (out.kind === 'uploaded') {
          await save(markDone(item));
          // The bytes are safe on the server now. Holding the Blob after that
          // is how a device fills up over a busy week.
          await storeRef.current.remove(item.id);
          setItems((prev) => prev.filter((x) => x.id !== item.id));
        } else {
          await save(markFailed(item, out.message, out.retryable, Date.now()));
        }
      } catch (err) {
        await save(markFailed(
          item, err instanceof Error ? err.message : String(err), true, Date.now()));
      } finally {
        busy.current = false;
      }
    };

    void tick();
    const id = setInterval(() => void tick(), TICK_MS);
    return () => clearInterval(id);
  }, [items, online, deps, save]);

  return { items, summary: summarise(items), online, enqueue, discard };
}
